<template>
  <div class="collection-information">
    <div class="information-section">
      <h5 class="section-title">ข้อมูลทั่วไป</h5>
      <FormGroup v-if="isNoSelectedType()">
        <h5 class="name">ประเภท</h5>
        <SelectOptionObject
          :disabledDeleteButton="true"
          :value="collectionType.find((option) => option.value === collection.type)"
          :options="collectionType"
          :label="'title'"
          :placeholder="'เลือกประเภท'"
          @onChanged="onSelectCollectionType"/>
      </FormGroup>
      <FormGroup class="with-help-text" v-if="isDynamicCollection()">
        <h5 class="name">แท็ก</h5>
        <small class="help-text">{{getCollectionTitle(collection)}}ที่ใช้แท็กนี้จะถูกนำเข้าโดยอัตโนมัติ</small>
          <MultiSelect
            :value="getCurrentTags"
            :isMultiple="true"
            :removeable="true"
            :options="getTagsResult"
            :customLabel="'name'"
            :placeholder="'เลือกแท็กที่ต้องการเพิ่ม หรือค้นหาด้วยชื่อแท็ก'"
            @onChanged="onTagChanged"
            @onSearch="debounceSearch"/>
      </FormGroup>
      <hr v-if="isDynamicCollection()">
      <FormGroup :errorMessage="saveCollectionStateError.key">
        <h5 class="name link-title">
          ลิงก์
          <i class="far fa-copy copy-icon ml-1" @click="copyMessage"/>
          <span class="copied-text" v-if="isCopied">คัดลอกแล้ว</span>
        </h5>
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text">{{`${baseURL}/playlists/`}}</span>
          </div>
          <input
            type="text"
            placeholder="ลิงก์"
            class="form-control"
            :class="{ 'is-invalid': saveCollectionStateError.key }"
            :value="collection.key"
            @blur="checkRequired($event, 'key', 'ระบุลิงก์')"/>
          <div class="invalid-feedback" v-if="saveCollectionStateError.key">
            {{ saveCollectionStateError.key }}
          </div>
        </div>
        <small class="help-text">กรุณากรอกเป็นภาษาอังกฤษเท่านั้น</small>
      </FormGroup>
      <FormGroup :errorMessage="saveCollectionStateError.title">
        <h5 class="name">ชื่อ</h5>
        <input
          type="text"
          placeholder="ชื่อ"
          class="form-control"
          :class="{ 'is-invalid': saveCollectionStateError.title }"
          :value="collection.title"
          @blur="checkRequired($event, 'title', 'ระบุชื่อ')"/>
        <div class="invalid-feedback" v-if="saveCollectionStateError.title">
          {{ saveCollectionStateError.title }}
        </div>
      </FormGroup>
      <FormGroup :errorMessage="saveCollectionStateError.programId" v-if="collection.type === 'video'">
        <h5 class="name">รายการ</h5>
        <SelectOptionObject
          :value="collection.program"
          :options="programs"
          :label="'title'"
          :placeholder="'เลือกรายการ'"
          :class="{'is-invalid': saveCollectionStateError.programId }"
          :removeable="false"
          @onChanged="onSelectedProgram"
          @onSearch="loadPrograms" />
        <div
          class="invalid-feedback"
          v-if="saveCollectionStateError.programId">
          กรุณาเลือกรายการ
        </div>
      </FormGroup>
      <FormGroup :errorMessage="saveCollectionStateError.contentCategory" v-if="collection.type === 'video'">
        <h5 class="name">หมวดหมู่คอนเทนท์</h5>
        <SelectOptionObject
          :class="{'is-invalid': saveCollectionStateError.contentCategory }"
          :value="collection.contentCategory"
          :disabledDeleteButton="true"
          :allowEmpty="false"
          :options="contentCategories"
          :disabled="hasContentCategory"
          :multiple="true"
          @onChanged="onSelectedContentCategory" />
        <div
          class="invalid-feedback"
          v-if="saveCollectionStateError.contentCategory">
          {{saveCollectionStateError.contentCategory}}
        </div>
      </FormGroup>
      <FormGroup>
        <h5 class="name">สถานะการเผยแพร่</h5>
        <ContentStatusManagement
          :status="collection.status"
          :startPublish="collection.startPublish"
          :endPublish="collection.endPublish"
          :onChange="onChangeStatus" />
      </FormGroup>
      <FormGroup class="with-help-text mobile-only mb-2">
        <input
          type="checkbox"
          class
          id="isPinned"
          @input="updateValue('isPinned', !collection.isPinned)"
          :checked="collection.isPinned"/>
        <label class="form-check-label ml-2" for="isPinned">ปักหมุดคอลเล็คชั่นนี้</label>
        <small class="help-text">ปักหมุดสำหรับคอลเล็คชั่นที่ใช้งานบ่อย เพื่อให้หาได้ง่าย</small>
      </FormGroup>
      <FormGroup class="image-section">
        <ImageField
          :aspectRatio="imageSize"
          @update:image="(image) => onChangeImage(image)"
          :image="getImage"
          :defaultImage="getDefaultImage">
          <div class="tab-container" slot="header">
            <span class="title">รูปปก</span>
            <div class="tab" :class="{'active': imageSize === '16:9'}" @click="onChangeImageSize('16:9')">16:9</div>
            <div class="tab" :class="{'active': imageSize === '9:16'}" @click="onChangeImageSize('9:16')">9:16</div>
            <div class="tab" :class="{'active': imageSize === '1:1'}" @click="onChangeImageSize('1:1')">1:1</div>
          </div>
        </ImageField>
      </FormGroup>
    </div>
    <div class="information-section">
      <h5 class="section-title">รายละเอียด</h5>
      <FormGroup
        class="with-help-text"
        :errorMessage="saveCollectionStateError.description"
        v-if="collection.type === 'video'">
        <h5 class="name">คำอธิบาย</h5>
        <textarea
          type="text"
          rows="3"
          class="form-control"
          :class="{ 'is-invalid': saveCollectionStateError.description }"
          placeholder="รายละเอียดเพลย์ลิสต์"
          @input="updateValue('description', $event.target.value)"
          :value="collection.description"/>
      </FormGroup>
      <FormGroup class="with-help-text" v-if="!isDynamicCollection()">
        <h5 class="name">แท็ก</h5>
          <MultiSelect
            :value="getCurrentTags"
            :isMultiple="true"
            :removeable="true"
            :options="getTagsResult"
            :customLabel="'name'"
            :placeholder="'เลือกแท็ก'"
            :taggable="true"
            :addTag="(tag) => updateValue('tags', [...getCurrentTags, tag])"
            @onChanged="(tags) => updateValue('tags', tags)"
            @onSearch="debounceSearch"/>
      </FormGroup>
      <FormGroup
        class="with-help-text"
        :errorMessage="saveCollectionStateError.note">
        <h5 class="name">บันทึก</h5>
        <small class="help-text">บันทึกสำหรับทีมงานภายใน</small>
        <textarea
          type="text"
          rows="3"
          class="form-control"
          :class="{ 'is-invalid': saveCollectionStateError.note }"
          placeholder="บันทึก"
          @input="updateValue('note', $event.target.value)"
          :value="collection.note"/>
      </FormGroup>
      <FormGroup v-if="collection && collection.createdBy">
        <h5 class="name">สร้างโดย</h5>
        <EditorInformation :editor="collection.createdBy" :timeStamp="collection.createdAt" />
      </FormGroup>
      <FormGroup v-if="collection && collection.updatedBy">
        <h5 class="name">แก้ไขล่าสุดโดย</h5>
        <EditorInformation :editor="collection.updatedBy" :timeStamp="collection.updatedAt" />
      </FormGroup>
    </div>
    <!-- playlist -->
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { loadPrograms } from '@/program/api';
import { loadTaxonomyByKey } from '@/taxonomy/api';
import FormGroup from '@/components/FormGroup.vue';
import SelectOptionObject from '@/components/SelectOptionObject.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import ImageField from '@/image/ImageField.vue';
import ContentStatusManagement from '@/contentStatus/ContentStatusManagement.vue';
import EditorInformation from '@/components/EditorInformation.vue';

export default {
  props: {
    saveCollectionStateError: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    FormGroup,
    SelectOptionObject,
    MultiSelect,
    ImageField,
    ContentStatusManagement,
    EditorInformation,
  },
  data() {
    return {
      tagValue: [],
      tagOptions: [],
      sortValue: null,
      programs: [],
      contentCategories: [],
      sortOptions: [
        { name: 'ใหม่ที่สุดไปเก่าที่สุด', code: 'newest' },
        { name: 'เก่าที่สุดไปใหม่ที่สุด', code: 'oldest' },
      ],
      collectionType: [
        { id: 1, title: 'เพลย์ลิสต์', value: 'video' },
        { id: 2, title: 'ชุดเพลย์ลิสต์', value: 'playlist' },
        { id: 3, title: 'ชุดตอน', value: 'episode' },
        { id: 4, title: 'ชุดรายการ', value: 'program' },
      ],
      imageSize: '16:9',
      isCopied: false,
    };
  },
  created() {
    this.searchTags();
    this.loadPrograms();
    this.loadContentCategories();
    this.tagValue = _.cloneDeep(this.collection.tags);
  },
  computed: {
    ...mapGetters(['collection', 'getTagsResult', 'getCurrentTags']),
    baseURL() {
      const env = process.env.NODE_ENV;
      if (env === 'production') {
        return 'https://altv.tv';
      }
      return process.env.VUE_APP_BASEURL || window.location.origin;
    },
    collectionId() {
      return this.$route.params.collectionId;
    },
    isDynamic() {
      return false;
    },
    contentCategoryId() {
      return this.collection.contentCategoryId;
    },
    hasContentCategory() {
      const category = _.get(this.collection, 'program.contentCategoryId', null);
      if (category) return true;
      return false;
    },
    getImage() {
      if (this.imageSize === '16:9') {
        return this.collection.coverImageCustom;
      } if (this.imageSize === '1:1') {
        return this.collection.squareImageCustom;
      } if (this.imageSize === '9:16') {
        return this.collection.verticalImageCustom;
      }
      return null;
    },
    getDefaultImage() {
      if (this.imageSize === '16:9') {
        return this.collection.coverImage;
      }
      return null;
    },
  },
  watch: {
    contentCategoryId(newValue) {
      if (!newValue && this.collection.type === 'video') {
        this.$store.dispatch('setCollectionErrorMessage', { field: 'contentCategory', message: 'กรุณาใส่หมวดหมู่คอนเทนต์' });
      } else {
        this.$store.dispatch('setCollectionErrorMessage', { field: 'contentCategory', message: '' });
      }
    },
  },
  methods: {
    async loadPrograms(filter) {
      const response = await loadPrograms(filter);
      this.programs = response.data.data;
    },
    async loadContentCategories() {
      const response = await loadTaxonomyByKey('content-category');
      this.contentCategories = response.data.data;
    },
    onChangeImageSize(newSize) {
      this.imageSize = newSize;
    },
    updateValue(attrName, attrValue) {
      this.$store.dispatch('setCollectionAttribute', { key: attrName, value: attrValue });
    },
    isDynamicCollection() {
      return this.collection.isDynamic;
    },
    getCollectionTitle(collection) {
      if (collection && collection.type) {
        const collectionType = _.get(collection, 'type', null).toLowerCase();
        switch (collectionType) {
          case 'video':
            return 'เพลย์ลิสต์';
          case 'playlist':
            return 'ชุดเพลย์ลิสต์';
          case 'episode':
            return 'ชุดตอน';
          case 'program':
            return 'ชุดรายการ';
          default:
            return 'คอลเล็คชั่น';
        }
      }
      return 'คอลเล็คชั่น';
    },
    debounceSearch(event) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.searchTags(event);
      }, 500);
    },
    async searchTags(filter) {
      this.$store.dispatch('searchTags', filter);
    },
    onTagChanged(tag) {
      this.$emit('onTagChanged', tag);
    },
    onSelectedProgram(program) {
      if (program) {
        const data = {
          ...this.collection,
          contentCategory: _.get(program, 'contentCategory', this.collection.contentCategory),
          contentCategoryId: _.get(program, 'contentCategoryId', this.collection.contentCategoryId),
          program,
          programId: program.id,
        };
        this.$store.dispatch('setCollection', data);
      } else {
        const data = {
          ...this.collection,
          contentCategory: null,
          contentCategoryId: null,
          program: null,
          programId: null,
        };
        this.$store.dispatch('setCollection', data);
      }
    },
    onSelectedContentCategory(category) {
      if (category) {
        this.$store.dispatch('setCollection', { ...this.collection, contentCategory: category, contentCategoryId: category.id });
      }
    },
    checkRequired(event, fieldName) {
      this.updateValue(fieldName, event.target.value);
    },
    onSelectCollectionType(collectionType) {
      this.updateValue('type', collectionType.value);
    },
    isNoSelectedType() {
      if (this.collectionId === 'create') {
        return true;
      }
      return false;
    },
    onChangeStatus(newStatus) {
      const { status, startPublish, endPublish } = newStatus;
      this.updateValue('status', status);
      this.updateValue('startPublish', startPublish);
      this.updateValue('endPublish', endPublish);
    },
    onChangeImage(image) {
      if (this.imageSize === '16:9') {
        this.updateValue('coverImageCustom', image);
      } else if (this.imageSize === '1:1') {
        this.updateValue('squareImageCustom', image);
      } else if (this.imageSize === '9:16') {
        this.updateValue('verticalImageCustom', image);
      }
    },
    copyMessage() {
      this.isCopied = true;
      try {
        const tempInput = document.createElement('textarea');
        document.body.appendChild(tempInput);
        tempInput.value = `${this.baseURL}/playlists/${this.collection.key}`;
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        setTimeout(() => {
          this.isCopied = false;
        }, 1000);
      } catch (err) {
        setTimeout(() => {
          this.isCopied = false;
        }, 1000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/variables.scss";
@import "./src/assets/scss/themes/components/form.scss";
@import "./src/assets/scss/themes/components/section-container.scss";

.collection-information {
  margin-top: $spacer * 1.75;
}

.section-title {
  margin-bottom: $spacer * 1.25;
  font-weight: normal;
}

.information-section {
  margin-bottom: $spacer * 3;
}

.tab-container {
  display: flex;
  align-items: center;
  padding: $spacer * 0.5 $spacer * 0.25;
  // background: white;
  // border: 1px solid $gray-300;
  // border-radius: 0.25px;
  .tab {
    padding: 0 $spacer * 0.75;
    font-weight: bold;
    cursor: pointer;
    border-right: 1px solid $gray-400;
    color: $gray-500;
    &:last-child {
      border: 0;
    }
    &.active {
      // color: lighten($primary, 10%);
      color: $altv-blue
        // padding: 0;
        // padding-left: $spacer * 0.5;
        // padding-right: $spacer * 0.5;
        // padding-bottom: calc(1rem - 2px) !important;
        // border-bottom: 2px solid $primary;
    }
  }
}

.input-group-text {
  font-size: 0.75rem;
  padding: 0.375rem 0.5rem
}

.link-title {
  .copied-text {
    margin-left: $spacer * 0.5;
    font-size: $font-size-sm;
    color: $gray-700;
    font-weight: bold;
  }
  .copy-icon {
    cursor: pointer;
  }
}


.mobile-only {
  display: none;
}

@media (max-width: 575.98px) {
  .mobile-only {
    display: unset;
  }
}
</style>
